import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "480",
  "slug": "how-to-spot-deforestation-from-satellite-imagery",
  "title": "How to Spot Deforestation From Satellite Imagery",
  "excerpt": "Satellites are how the world tracks vanishing forests. Here is how to recognise deforestation from above, from telltale fishbone patterns to the colour shifts that reveal cleared land.",
  "date": "2026-06-18",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1686005232385-831dac1d0458?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxkZWZvcmVzdGF0aW9uJTIwYWVyaWFsJTIwZm9yZXN0fGVufDF8MHx8fDE3ODE3NzA0Mjh8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "Forests vanish faster than anyone can walk through them, which is why satellites have become the primary way we watch the world's woodlands. From orbit, the signs of clearing are often unmistakable once you know what to look for. Reading them is a genuinely useful skill, and a fascinating window into how humans are reshaping the planet. Here is how deforestation reveals itself from above."
    },
    {
      "type": "heading",
      "text": "The Colour Change"
    },
    {
      "type": "paragraph",
      "text": "The most basic signal is colour. Healthy forest reads as deep, slightly textured green, the canopy a rough, broccoli-like surface. When forest is cleared, that green gives way to the tan, brown, or pale pink of bare soil, or the flatter, lighter green of pasture and cropland. A sharp edge between dark forest and pale cleared ground is one of the clearest fingerprints of recent deforestation."
    },
    {
      "type": "paragraph",
      "text": "Analysts often switch to false-colour imagery to make the contrast even starker. By mapping near-infrared light, which living vegetation reflects strongly, to a visible colour, healthy forest glows bright while cleared land stays dull. Vegetation indices like NDVI turn that idea into a number, so a drop in the index over time flags forest loss automatically."
    },
    {
      "type": "heading",
      "text": "The Fishbone Pattern"
    },
    {
      "type": "paragraph",
      "text": "In tropical rainforests, especially the Amazon, deforestation often follows a distinctive geometry. A road is cut into intact forest, then side roads branch off it at regular intervals, and clearing spreads outward from each of those. Seen from above, the result looks exactly like a fishbone or a herringbone: a central spine with ribs of cleared land projecting from both sides. Spot a fishbone and you are almost certainly looking at organised, road-led deforestation."
    },
    {
      "type": "heading",
      "text": "Straight Edges and Geometric Shapes"
    },
    {
      "type": "paragraph",
      "text": "Nature rarely draws straight lines. Forests have ragged, organic boundaries that follow rivers, ridges, and soil changes. Human clearing, by contrast, tends to be geometric, rectangular plantations, blocky clear-cuts, and dead-straight property lines. When you see crisp right angles and ruler-straight edges in what should be wild forest, that regularity is a strong sign of human activity rather than a natural opening."
    },
    {
      "type": "list",
      "items": [
        "Rectangular clear-cuts and plantation blocks point to logging or agriculture.",
        "Straight boundaries between forest and field follow human property and administrative lines.",
        "A grid of identical plots suggests organised settlement or industrial agriculture, such as oil palm or soy.",
        "Ragged, irregular gaps are more likely natural, from storms, landslides, or river shifts."
      ]
    },
    {
      "type": "heading",
      "text": "Smoke, Burn Scars, and Roads"
    },
    {
      "type": "paragraph",
      "text": "Clearing is frequently done by burning, so active deforestation can show up as plumes of smoke and, afterward, dark charred burn scars on the ground. New roads pushing into untouched forest are an early warning that clearing is about to follow, since access almost always precedes large-scale removal. Together, roads, fire, and fresh bare soil tell a sequence: open the forest, burn what was felled, and convert the land."
    },
    {
      "type": "heading",
      "text": "The Power of Comparing Over Time"
    },
    {
      "type": "paragraph",
      "text": "The single most powerful technique is comparison. Because satellites like the Landsat and Sentinel programs photograph the same places repeatedly, you can line up images from different years and watch the forest retreat frame by frame. A green tile that turns brown between one year and the next is deforestation made visible. This time-lapse approach is how scientists measure forest loss at a global scale and how watchdogs catch illegal clearing close to real time."
    },
    {
      "type": "heading",
      "text": "Telling Deforestation From Natural Change"
    },
    {
      "type": "paragraph",
      "text": "Not every gap in the canopy is deforestation. Wildfires, floods, pests, and seasonal leaf-fall all change how forests look from space. The clues that point to human clearing are the ones above: straight lines, fishbone roads, geometric plots, and a permanent shift from forest to farmland or pasture rather than a temporary disturbance that grows back. Learning to tell the difference is what separates a casual glance from genuine satellite literacy."
    }
  ]
};
